import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "./ThemeContext"; // Import ThemeProvider
import StartPage from "./StartPage";
import LeftMenu from "./LeftMenu";
import CreateRankings from "./CreateRankings";
import DraftSetup from "./DraftSetup";
import DraftHelper from "./DraftHelper";
import DraftsList from "./DraftsList";
import LeagueList from "./LeagueList";
import BestballList from "./BestballList";
import TradeHelperPage from "./TradeHelperPage";
import TradeTracker from "./TradeTracker";
import DFS from "./DFS";
import DFSResults from "./DFSResults";
import DFSManage from "./DFSManage";
import Tournaments from "./Tournaments";
import TournamentCreate from "./TournamentCreate";
import TournamentResults from "./TournamentResults";
import Settings from "./Settings";
import { ChangelogWithReadMark } from "./Changelog";
import Stats from "./Stats";
import Odds from "./Odds";
import Gameday from "./Gameday";
import StartSit from "./StartSit";
import "./Layout.css";
import "./positionColors.css";
import "./LeftMenu.css";
import "./GenericStyles.css";
import { effectiveUserName } from "./settingsUser";
import { useSleeperAuth } from "./auth";

console.log({
  StartPage,
  LeftMenu,
  CreateRankings,
  DraftSetup,
  DraftHelper,
  DraftsList,
  LeagueList,
  BestballList,
  TradeHelperPage,
  Settings,
  Stats,
});

function App() {
  const [csvData, setCsvData] = useState(""); // Manage CSV data in App.js
  const [csvFileName, setCsvFileName] = useState(""); // Manage CSV data in App.js

  // The menu name drives every per-user route and the DFS tournament list, but
  // nothing here used to load it — only the home page did, on mount. Landing
  // anywhere else (a deep link, a bookmark, a reload on /dfs) left it empty,
  // which blanked the menu box and pointed Draft List, Leagues and Bestball at
  // "/" instead of at a page. That is why clicking one of those went Home, and
  // why clicking again worked: the trip Home mounted the one page that loaded
  // the name.
  const [userName, setUserName] = useState(() => effectiveUserName(""));

  // Signing in when no name is saved should fill it in. Only on the transition,
  // though — refilling whenever the box is empty would make it impossible to
  // clear by hand.
  const sleeperAuth = useSleeperAuth();
  const wasSignedIn = useRef(!!sleeperAuth?.token);
  useEffect(() => {
    const signedIn = !!sleeperAuth?.token;
    if (signedIn && !wasSignedIn.current && !userName && sleeperAuth.display_name) {
      setUserName(sleeperAuth.display_name);
    }
    wasSignedIn.current = signedIn;
  }, [sleeperAuth, userName]);

  return (
    <ThemeProvider>
      <Router>
        <div className="app-container">
          <LeftMenu userName={userName} setUserName={setUserName} />
          <div className="content">
            <Routes>
              {/* Route for the start page */}
              <Route
                path="/"
                element={
                  <StartPage userName={userName} setUserName={setUserName} />
                }
              />
              <Route
                path="/draftsetup"
                element={
                  <DraftSetup
                    setCsvData={setCsvData}
                    setCsvFileName={setCsvFileName}
                    userName={userName}
                    setUserName={setUserName}
                    isRankingsPage={false}
                  />
                }
              />

              {/* Route for the draft helper page */}
              <Route
                path="/drafthelper/:draftId"
                element={
                  <DraftHelper
                    csvData={csvData}
                    csvFileName={csvFileName} // Pass CSV data to DraftHelper
                  />
                }
              />
              <Route
                path="/drafthelper"
                element={
                  <DraftHelper
                    csvData={csvData}
                    csvFileName={csvFileName} // Pass CSV data to DraftHelper
                  />
                }
              />
              <Route
                path="/drafts/:userName"
                element={<DraftsList />} // Pass userName to DraftPage component
              />
              <Route path="/leagues/:userName" element={<LeagueList />} />

              <Route path="/bestball/:userName" element={<BestballList />} />
              <Route path="/trade-analyzer" element={<TradeHelperPage userName={userName} setUserName={setUserName} />} />
              <Route path="/trade-tracker" element={<TradeTracker />} />
              <Route path="/dfs" element={<DFS userName={userName} />} />
              <Route path="/dfs/manage" element={<DFSManage />} />
              <Route path="/dfs/results/tinyurl/:name" element={<DFSResults />} />
              <Route path="/dfs/results" element={<DFSResults />} />
              <Route path="/tournaments/create" element={<TournamentCreate userName={userName} />} />
              <Route path="/tournaments/:tournamentId/results" element={<TournamentResults />} />
              <Route path="/tournaments" element={<Tournaments />} />
              <Route
                path="/rankingssetup"
                element={
                  <DraftSetup
                    setCsvData={setCsvData}
                    setCsvFileName={setCsvFileName}
                    userName={userName}
                    setUserName={setUserName}
                    isRankingsPage={true}
                  />
                }
              />
              <Route
                path="/rankings"
                element={
                  <CreateRankings
                    csvData={csvData}
                    csvFileName={csvFileName} // Pass CSV data to DraftHelper
                  />
                }
              />
              <Route path="/gameday" element={<Gameday />} />
              <Route path="/startsit" element={<StartSit userName={userName} />} />
              <Route path="/stats" element={<Stats />} />
              <Route path="/odds" element={<Odds />} />
              <Route path="/changelog" element={<ChangelogWithReadMark />} />
              <Route path="/settings" element={<Settings />} />
            </Routes>
          </div>
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;
